import React from 'react';
import {
    Typography,
    Box,
    Chip,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemIcon
} from '@mui/material';
import {
    QuizOutlined as QuizIcon,
    CheckCircleOutline as CheckIcon
} from '@mui/icons-material';
import PopupBase from '../../../popup/base/PopupBase';
import CustomButton from '../../../components/buttons/CustomButton';

const AnamnesisPreviewModal = ({ 
    open, 
    template, 
    onClose 
}) => {
    if (!template || !open) return null;

    const questions = JSON.parse(template.questions || '[]');

    const getTypeLabel = (type) => {
        const typeLabels = {
            'text': 'Texto',
            'textarea': 'Texto Longo',
            'select': 'Seleção',
            'radio': 'Múltipla Escolha',
            'checkbox': 'Caixas de Seleção',
            'date': 'Data',
            'number': 'Número',
            'email': 'Email',
            'phone': 'Telefone'
        };
        return typeLabels[type] || type;
    };

    const getTypeColor = (type) => {
        const typeColors = {
            'text': 'primary',
            'textarea': 'secondary',
            'select': 'success',
            'radio': 'warning',
            'checkbox': 'info',
            'date': 'error',
            'number': 'primary',
            'email': 'secondary',
            'phone': 'success'
        };
        return typeColors[type] || 'default';
    };

    const renderContent = () => (
        <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
            {/* Header do Template */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h5" gutterBottom>
                    {template.label}
                </Typography>
                
                {template.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {template.description}
                    </Typography>
                )}

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Chip
                        label={`${questions.length} ${questions.length === 1 ? 'pergunta' : 'perguntas'}`}
                        color="primary"
                        size="small"
                        icon={<QuizIcon />}
                    />
                    {template.category && (
                        <Chip
                            label={template.category}
                            variant="outlined"
                            size="small"
                        />
                    )}
                </Box>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Lista de Perguntas */}
            {questions.length > 0 ? (
                <List sx={{ p: 0 }}>
                    {questions.map((question, index) => (
                        <ListItem
                            key={index}
                            sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                mb: 1,
                                bgcolor: 'background.paper'
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: 40 }}>
                                <Box
                                    sx={{
                                        width: 24,
                                        height: 24,
                                        borderRadius: '50%',
                                        bgcolor: 'primary.main',
                                        color: 'white',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '0.75rem',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    {index + 1}
                                </Box>
                            </ListItemIcon>
                            <ListItemText
                                primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                        <Typography variant="subtitle1" sx={{ flex: 1 }}>
                                            {question.label}
                                        </Typography>
                                        <Chip
                                            label={getTypeLabel(question.type)}
                                            size="small"
                                            color={getTypeColor(question.type)}
                                            variant="outlined"
                                        />
                                    </Box>
                                }
                                secondary={
                                    <Box>
                                        {question.description && (
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                {question.description}
                                            </Typography>
                                        )}
                                        
                                        {question.options && question.options.length > 0 && (
                                            <Box sx={{ mt: 1 }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    Opções:
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                                    {question.options.map((option, optIndex) => (
                                                        <Chip
                                                            key={optIndex}
                                                            label={option}
                                                            size="small"
                                                            variant="outlined"
                                                            sx={{ fontSize: '0.7rem' }}
                                                        />
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}
                                        
                                        {question.required && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                                                <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
                                                <Typography variant="caption" color="success.main">
                                                    Campo obrigatório
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                }
                            />
                        </ListItem>
                    ))}
                </List>
            ) : (
                <Box sx={{ 
                    textAlign: 'center', 
                    py: 4,
                    color: 'text.secondary'
                }}>
                    <QuizIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                    <Typography variant="body1">
                        Nenhuma pergunta cadastrada neste template
                    </Typography>
                </Box>
            )}

            {/* Footer com botão de fechar */}
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'flex-end', 
                mt: 3,
                pt: 2,
                borderTop: '1px solid',
                borderColor: 'divider'
            }}>
                <CustomButton
                    style="secondary"
                    variant="contained"
                    label="Fechar"
                    onClick={onClose}
                />
            </Box>
        </Box>
    );

    return (
        <PopupBase
            Title="Visualizar modelo"
            Icon={<QuizIcon />}
            OnClose={onClose}
            Content={renderContent()}
            maxWidth="md"
        />
    );
};

export default AnamnesisPreviewModal;